import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { Observable, BehaviorSubject } from 'rxjs';
import { HeaderHelperService } from '../header-helper.service';
import { FilterModel } from '../../models/filters/filters.model';
import { SortModel } from '../../models/filters copy/sort.model';
import { CaseActivityLog } from '../../models/patient/case-activity-log.model';

@Injectable({
  providedIn: 'root',
})
export class PatientsService {
  constructor(
    private http: HttpClient,
    private headerHelperService: HeaderHelperService
  ) {}

  private patientIdSubject = new BehaviorSubject<string>('');
  patientId$ = this.patientIdSubject.asObservable();

  setPatientId(id: string) {
    this.patientIdSubject.next(id);
  }

  getPatientId(): string {
    return this.patientIdSubject.getValue();
  }

  patientTest(page: number, size: number): Observable<any> {
    let url = `${environment.apiLocations.patient}/Patient/test`;

    return this.http.get<any>(url, {
      headers: this.headerHelperService.getHeaders(),
    });
  }

  patientList(
    page: number,
    size: number,
    filters?: FilterModel[],
    sort?: SortModel
  ): Observable<any> {
    let url = `${
      environment.apiLocations.patient
    }/Patient?PageRequest.PageIndex=${page - 1}&PageRequest.PageSize=${size}`;

    if (filters) {
      const resultFilter = filters.filter((x) => !!x.value);
      url += resultFilter.map((f) => `&${f.filterId}=${f.value}`).join('');
    }
    if (sort) {
      url += `&SortBy=${sort.column}&SortDescending=${
        sort.orderBy == 'descending'
      }`;
    }

    return this.http.get<any>(url, {
      headers: this.headerHelperService.getHeaders(),
    });
  }

  patientDeteail(id: string): Observable<any> {
    let url = `${environment.apiLocations.patient}/Patient/${id}`;

    return this.http.get<any>(url, {
      headers: this.headerHelperService.getHeaders(),
    });
  }

  consultationTimeLineByPatientId(patientId: string): Observable<any> {
    let url = `${environment.apiLocations.patient}/PatientRecordCase/GetConsultationTimeLineList/${patientId}`;

    return this.http.get<any>(url, {
      headers: this.headerHelperService.getHeaders(),
    });
  }

  createDiabetesPatientRecord(caseData: any): Observable<any> {
    return this.http.post<any>(
      `${environment.apiLocations.patient}/PatientRecord/CreateDiabetesPatientRecord`,
      caseData,
      {
        headers: this.headerHelperService.getHeaders(),
      }
    );
  }

  createDiabetesPatientRecordCase(caseData: any): Observable<any> {
    return this.http.post<any>(
      `${environment.apiLocations.patient}/PatientRecordCase/CreateDiabetesPatientRecordCase`,
      caseData,
      {
        headers: this.headerHelperService.getHeaders(),
      }
    );
  }

  patientRecordCaseDetail(patientRecordCaseId: string): Observable<any> {
    let url = `${environment.apiLocations.patient}/PatientRecordCase/GetPatientRecordCaseDetail/${patientRecordCaseId}`;

    return this.http.get<any>(url, {
      headers: this.headerHelperService.getHeaders(),
    });
  }

  patientRecordCaseImages(patientRecordCaseId: string): Observable<any> {
    let url = `${environment.apiLocations.patient}/PatientRecordCase/GetPatientRecordCaseImages/${patientRecordCaseId}`;

    return this.http.get<any>(url, {
      headers: this.headerHelperService.getHeaders(),
    });
  }

  consultationTimeLineByCaseId(patientRecordCaseId: string): Observable<any> {
    let url = `${environment.apiLocations.patient}/PatientRecordCase/GetConsultationTimeLineByCaseId/${patientRecordCaseId}`;

    return this.http.get<any>(url, {
      headers: this.headerHelperService.getHeaders(),
    });
  }

  updateSelectedPatientRecordCaseImage(
    patientRecordCaseId: string
  ): Observable<any> {
    let url = `${environment.apiLocations.patient}/PatientRecordCase/UpdateSelectedPatientRecordCaseImage/${patientRecordCaseId}`;

    return this.http.put<any>(
      url,
      {},
      { headers: this.headerHelperService.getHeaders() }
    );
  }

  consultationTimeLineListByPatientRecordCaseId(
    patientRecordCaseId: string
  ): Observable<any> {
    let url = `${environment.apiLocations.patient}/PatientRecordCase/GetConsultationTimeLineListByPatientRecordCaseId/${patientRecordCaseId}`;

    return this.http.get<any>(url, {
      headers: this.headerHelperService.getHeaders(),
    });
  }

  closePatientRecordCase(
    patientRecordCaseId: string,
    form: any
  ): Observable<any> {
    let url = `${environment.apiLocations.patient}/PatientRecordCase/ClosePatientRecordCase/${patientRecordCaseId}`;

    return this.http.put<any>(url, form, {
      headers: this.headerHelperService.getHeaders(),
    });
  }

  patientInformationByPID(id: string): Observable<any> {
    let url = `${environment.apiLocations.patient}/Patient/GetPatientInformation/${id}`;

    return this.http.get<any>(url, {
      headers: this.headerHelperService.getHeaders(),
    });
  }

  dashboardCountInformations(): Observable<any> {
    let url = `${environment.apiLocations.patient}/Dashboard/GetDashboardCountInformation`;

    return this.http.get<any>(url, {
      headers: this.headerHelperService.getHeaders(),
    });
  }

  qrCodeForPatientDetail(id: string): Observable<Blob> {
    let url = `${environment.apiLocations.patient}/QrCode/GetQRCode/${id}`;

    return this.http.get<Blob>(url, {
      headers: this.headerHelperService.getHeaders(),
      responseType: 'blob' as 'json',
    });
  }

  getPatientRecordCaseDetail(patientRecordCaseId: string): Observable<any> {
    let url = `${environment.apiLocations.patient}/PatientRecordCase/GetPatientRecordCaseDetail/${patientRecordCaseId}`;

    return this.http.get<any>(url, {
      headers: this.headerHelperService.getHeaders(),
    });
  }

  getCaseActivityLogsByType(
    patientRecordCaseId: string,
    actionType: number
  ): Observable<CaseActivityLog[]> {
    const url = `${environment.apiLocations.patient}/CaseActivityLog/${patientRecordCaseId}/type/${actionType}`;

    return this.http.get<CaseActivityLog[]>(url, {
      headers: this.headerHelperService.getHeaders(),
    });
  }
}
